import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { OrganizationOption } from "../organization.types";
import type { OptionType } from "@core/types/select.types";

export type UseFetchOrganizationsResult = {
  /** Organization options formatted for select components */
  options: OptionType[];
  /** Raw organization data from API */
  organizations: OrganizationOption[];
  /** Whether the query is loading */
  isLoading: boolean;
  /** Error if the query failed */
  error: Error | null;
};

/**
 * Fetch approved organizations for select dropdowns.
 *
 * Returns only organizations where is_approved=true.
 * Results are cached for 5 minutes since organizations don't change often.
 *
 * @param enabled - Whether to enable the query. Defaults to true. Pass false to
 *                  prevent the query from running (useful for dialogs that shouldn't
 *                  fetch until opened).
 *
 * @example
 * ```tsx
 * const { options, isLoading } = useFetchOrganizations();
 *
 * <Select
 *   options={options}
 *   isLoading={isLoading}
 *   // ...
 * />
 * ```
 */
export function useFetchOrganizations(
  enabled: boolean = true
): UseFetchOrganizationsResult {
  const { data, isLoading, error } = useQuery<OrganizationOption[]>({
    queryKey: ["organizations", "affiliations"],
    queryFn: async () => {
      return apiRequest("/organizations/affiliations/", "GET") as Promise<
        OrganizationOption[]
      >;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - organizations don't change often
    enabled,
  });

  const organizations = data ?? [];

  // Convert to OptionType format for select components
  const options: OptionType[] = organizations.map((org) => ({
    label: org.name,
    value: org.id.toString(),
    data: org,
  }));

  return {
    options,
    organizations,
    isLoading,
    error: error as Error | null,
  };
}
